import UiLoader from "@/react/story/components/ui/UiLoader";
import { useConfig } from "@/react/story/providers/ConfigContext";
import type { Upload } from "@/react/story/types";
import UploadUtils from "@/react/story/utils/UploadUtils";
import Dropzone from "dropzone";
import { type ReactNode, useEffect, useRef, useState } from "react";

type Props = {
    defaultValue: Upload | null | undefined;
    onChange: (file: Upload | null) => void;
    canDelete?: boolean;
    forceDelete?: boolean;
    acceptedFiles?: string;
    icon?: string;
    children?: ReactNode;
}

export default function UiDropzone(props: Props) {
    const Config = useConfig();

    const ref = useRef<HTMLDivElement>(null);

    const [ message, setMessage ] = useState<string | null>(null);
    const [ isLoading, setIsLoading ] = useState<boolean>(false);
    const [ error, setError ] = useState<string | null>(null);
    const [ file, setFile ] = useState<Upload | null>(props.defaultValue ?? null);
    const [ isDisabled, setIsDisabled ] = useState<boolean>(false);
    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;

    const reset = () => {
        setMessage(null);
        setIsLoading(false);
        setError(null);
    };

    const saveFile = (upload: Upload | null) => {
        setFile(upload);
        onChangeRef.current(upload);
    };

    const defineExternalLink = () => {
        const file = UploadUtils.getExternalFile();
        if (file) {
            saveFile(file);
        }
    };

    useEffect(() => {
        setFile(props.defaultValue ?? null);
    }, [ props.defaultValue ]);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const d = !!ref.current.closest("fieldset[disabled]");
        setIsDisabled(d);
        if (d) {
            return;
        }

        const dropzone = new Dropzone(ref.current, {
            url: Config.temporaryEndpoint,
            paramName: "temporary",
            uploadMultiple: false,
            clickable: true,
            createImageThumbnails: false,
            previewTemplate: "<div></div>",
            acceptedFiles: props.acceptedFiles ?? "image/*",
            timeout: 0,
            dragenter: () => {
                setError(null);
                setMessage("Relachez la souris pour envoyer le fichier");
            },
            dragleave: () => {
                setMessage(null);
            },
            sending: () => {
                setError(null);
                setFile(null);
                setIsLoading(true);
            },
            uploadprogress: (_, progress) => {
                setMessage(`Téléversement : ${Math.round(progress)}%`);
            },
            canceled: () => {
                reset();
                setError("Le téléversement du fichier a été annulé.");
            },
            error: (_, errorMessage) => {
                reset();
                if ("string" === typeof errorMessage) {
                    setError(errorMessage);
                } else if ("object" === typeof errorMessage && null !== errorMessage) {
                    // the server answers { error: "…" }; dropzone's own failures carry { message: "…" }
                    setError((errorMessage as { error?: string }).error ?? errorMessage.message);
                } else {
                    setError("Une erreur s'est produite.");
                }
            },
        });

        // registered through on() rather than as an option: @types/dropzone declares
        // the `success` option with the file only, without the response payload
        dropzone.on("success", (_, response) => {
            reset();
            const upload = "string" === typeof response ? JSON.parse(response) as Upload : response as Upload;
            saveFile(upload);
        });

        return () => {
            dropzone.destroy();
        };
    }, []);

    return (
        <div className={`temporary-uploader ${isLoading ? "has-warning" : ""} ${error ? "has-error" : ""}`}>
            <div className="input-group temporary-uploader-shadow">
                <div className="input-group-text">
                    {isLoading ? (
                        <UiLoader />
                    ) : (
                        <span className={`bi-${props.icon ?? "image"}`}></span>
                    )}
                </div>
                <div className={`form-control ${isDisabled ? " disabled" : ""}`} ref={ref}>
                    {error ?? message ?? file?.clientFilename ?? "Cliquez ou glissez votre fichier ici"}
                </div>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    title="Lien externe"
                    onClick={defineExternalLink}
                >
                    <span className="bi-link"></span>
                </button>
                {(props.forceDelete ?? false) || (file && props.canDelete) ? (
                    <button
                        type="button"
                        className="btn btn-outline-danger"
                        onClick={() => saveFile(null)}
                    >
                        <span className="bi-trash"></span>
                    </button>
                ) : null}
                {props.children}
            </div>
        </div>
    );
}
